import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Table, Badge, Alert, Spinner, Modal, Nav, Tabs, Tab, InputGroup } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaDownload, FaFileAlt, FaCheck, FaList, FaQuestionCircle, FaClock, FaGraduationCap, FaUser, FaChartBar, FaSearch, FaEye, FaSave, FaTimes, FaPrint } from 'react-icons/fa';
import { getDatabase, ref, set, push, get, remove, update, query, orderByChild, child } from 'firebase/database';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const ExamManagement = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const db = getDatabase();
  const auth = getAuth();

  const [activeTab, setActiveTab] = useState('createExam');
  const [loading, setLoading] = useState(false);
  const [exams, setExams] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [results, setResults] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedExamForResults, setSelectedExamForResults] = useState(null);
  const [showResultDetails, setShowResultDetails] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [studentSubmissions, setStudentSubmissions] = useState([]);
  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showEditQuestionModal, setShowEditQuestionModal] = useState(false);
  
  const [examFormData, setExamFormData] = useState({
    examName: '',
    subject: '',
    date: '',
    duration: '',
    totalMarks: '',
    passingMarks: '',
    class: '',
    section: ''
  });

  const [questionFormData, setQuestionFormData] = useState({
    question: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctAnswer: 'A',
    marks: 1
  });

  const tabs = [
    { id: 'createExam', label: 'Create Exam', icon: <FaPlus /> },
    { id: 'manageExams', label: 'Manage Exams', icon: <FaList /> },
    { id: 'paperCreation', label: 'Paper Creation', icon: <FaQuestionCircle /> },
    { id: 'studentSubmissions', label: 'Student Submissions', icon: <FaFileAlt /> },
    { id: 'results', label: 'Results', icon: <FaChartBar /> },
    { id: 'admitCards', label: 'Admit Cards', icon: <FaDownload /> }
  ];

  // Fetch exams on component mount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      fetchExams();
      fetchStudents();
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (selectedExam) {
      fetchQuestions(selectedExam.id);
    }
  }, [selectedExam]);

  const fetchExams = async () => {
    setLoading(true);
    setError('');
    try {
      const examRef = ref(db, 'exams');
      const snapshot = await get(examRef);
      
      if (snapshot.exists()) {
        const examList = [];
        snapshot.forEach((childSnapshot) => {
          examList.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        setExams(examList.sort((a, b) => new Date(b.date) - new Date(a.date)));
      } else {
        setExams([]);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(`Error fetching exams: ${err.message}`);
    }
    setLoading(false);
  };

  const fetchQuestions = async (examId) => {
    setLoading(true);
    try {
      const questionsRef = ref(db, `exams/${examId}/questions`);
      const snapshot = await get(questionsRef);
      
      if (snapshot.exists()) {
        const questionList = [];
        snapshot.forEach((childSnapshot) => {
          questionList.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        setQuestions(questionList);
      } else {
        setQuestions([]);
      }
    } catch (err) {
      setError('Error fetching questions: ' + err.message);
    }
    setLoading(false);
  };

  const fetchStudents = async () => {
    try {
      const studentsRef = ref(db, 'admissions');
      const snapshot = await get(studentsRef);
      
      if (snapshot.exists()) {
        const studentList = [];
        snapshot.forEach((childSnapshot) => {
          const student = childSnapshot.val();
          studentList.push({
            id: childSnapshot.key,
            ...student
          });
        });
        setStudents(studentList);
      } else {
        setStudents([]);
      }
    } catch (err) {
      console.error('Error fetching students:', err);
    }
  };

  const fetchStudentSubmissions = async (examId) => {
    setLoading(true);
    setError('');
    try {
      const submissionsRef = ref(db, 'examResults');
      const snapshot = await get(submissionsRef);
      
      if (snapshot.exists()) {
        const submissionList = [];
        snapshot.forEach((childSnapshot) => {
          const submission = childSnapshot.val();
          
          if (submission.examId === examId) {
            submissionList.push({
              id: childSnapshot.key,
              ...submission
            });
          }
        });

        // Enrich submissions with student data
        const enrichedSubmissions = await Promise.all(
          submissionList.map(async (submission) => {
            const student = students.find(s => 
              s.email === submission.studentEmail || 
              s.userId === submission.studentId ||
              s.name === submission.studentName
            );
            
            return {
              ...submission,
              studentName: student?.name || submission.studentName || 'Unknown Student',
              studentClass: student?.class || submission.studentClass || 'N/A',
              studentSection: student?.section || submission.studentSection || 'N/A',
              studentRollNumber: student?.rollNumber || submission.studentRollNumber || 'N/A',
              studentId: student?.id || submission.studentId
            };
          })
        );
        
        setStudentSubmissions(enrichedSubmissions);
        setSelectedExamForResults(exams.find(exam => exam.id === examId));
        setShowSubmissionsModal(true);
        setSuccess(`Loaded ${enrichedSubmissions.length} student submissions`);
      } else {
        setStudentSubmissions([]);
        setError('No student submissions found for this exam.');
      }
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError('Error fetching submissions: ' + err.message);
    }
    setLoading(false);
  };

  const fetchResults = async (examId) => {
    setLoading(true);
    setError('');
    try {
      const resultsRef = ref(db, 'examResults');
      const snapshot = await get(resultsRef);
      
      if (snapshot.exists()) {
        const resultList = [];
        snapshot.forEach((childSnapshot) => {
          const result = childSnapshot.val();
          
          if (result.examId === examId) {
            resultList.push({
              id: childSnapshot.key,
              ...result
            });
          }
        });

        const enrichedResults = await Promise.all(
          resultList.map(async (result) => {
            const student = students.find(s => 
              s.email === result.studentEmail || 
              s.userId === result.studentId ||
              s.name === result.studentName
            );
            
            return {
              ...result,
              studentName: student?.name || result.studentName || 'Unknown Student',
              studentClass: student?.class || result.studentClass || 'N/A',
              studentSection: student?.section || result.studentSection || 'N/A',
              studentRollNumber: student?.rollNumber || result.studentRollNumber || 'N/A'
            };
          })
        );
        
        setResults(enrichedResults);
        setSelectedExamForResults(exams.find(exam => exam.id === examId));
        setSuccess(`Results loaded for ${exams.find(exam => exam.id === examId)?.examName}`);
      } else {
        setResults([]);
        setSelectedExamForResults(exams.find(exam => exam.id === examId));
        setError('No results found for this exam.');
      }
    } catch (err) {
      console.error('Error fetching results:', err);
      setError('Error fetching results: ' + err.message);
    }
    setLoading(false);
  };

  const handleCreateExam = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      setError('Please login to create exam');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const examRef = ref(db, 'exams');
      const newExamRef = push(examRef);
      const newExam = {
        ...examFormData,
        createdAt: new Date().toISOString(),
        status: 'Upcoming',
        totalQuestions: 0,
        createdBy: currentUser.uid
      };
      
      await set(newExamRef, newExam);
      setSuccess('Exam created successfully!');
      setExamFormData({
        examName: '',
        subject: '',
        date: '',
        duration: '',
        totalMarks: '',
        passingMarks: '',
        class: '',
        section: ''
      });
      fetchExams();
    } catch (err) {
      console.error('Create error:', err);
      setError(`Failed to create exam: ${err.message}`);
    }
    setLoading(false);
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      setError('Please login to add questions');
      return;
    }
    setLoading(true);
    setError('');
    
    try {
      const questionsRef = ref(db, `exams/${selectedExam.id}/questions`);
      const newQuestionRef = push(questionsRef);
      await set(newQuestionRef, questionFormData);
      
      const examRef = ref(db, `exams/${selectedExam.id}`);
      await update(examRef, {
        totalQuestions: (selectedExam.totalQuestions || 0) + 1
      });
      
      setSuccess('Question added successfully!');
      setQuestionFormData({
        question: '',
        optionA: '',
        optionB: '',
        optionC: '',
        optionD: '',
        correctAnswer: 'A',
        marks: 1
      });
      
      fetchQuestions(selectedExam.id);
      fetchExams();
    } catch (err) {
      setError('Error adding question: ' + err.message);
    }
    setLoading(false);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setQuestionFormData({
      question: question.question,
      optionA: question.optionA,
      optionB: question.optionB,
      optionC: question.optionC,
      optionD: question.optionD,
      correctAnswer: question.correctAnswer,
      marks: question.marks
    });
    setShowEditQuestionModal(true);
  };

  const handleUpdateQuestion = async (e) => {
    e.preventDefault();
    if (!currentUser || !editingQuestion) {
      setError('Please login and select a question to update');
      return;
    }
    setLoading(true);
    setError('');
    
    try {
      const questionRef = ref(db, `exams/${selectedExam.id}/questions/${editingQuestion.id}`);
      await update(questionRef, questionFormData);
      
      setSuccess('Question updated successfully!');
      setQuestionFormData({
        question: '',
        optionA: '',
        optionB: '',
        optionC: '',
        optionD: '',
        correctAnswer: 'A',
        marks: 1
      });
      setEditingQuestion(null);
      setShowEditQuestionModal(false);
      
      fetchQuestions(selectedExam.id);
    } catch (err) {
      setError('Error updating question: ' + err.message);
    }
    setLoading(false);
  };

  const handleDeleteExam = async (id) => {
    if (!currentUser) {
      setError('Please login to delete exam');
      return;
    }
    if (window.confirm('Are you sure you want to delete this exam?')) {
      setLoading(true);
      try {
        const examRef = ref(db, `exams/${id}`);
        await remove(examRef);
        setSuccess('Exam deleted successfully!');
        fetchExams();
      } catch (err) {
        setError('Error deleting exam: ' + err.message);
      }
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (examId, questionId) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      setLoading(true);
      try {
        const questionRef = ref(db, `exams/${examId}/questions/${questionId}`);
        await remove(questionRef);
        
        const examRef = ref(db, `exams/${examId}`);
        const examSnapshot = await get(examRef);
        if (examSnapshot.exists()) {
          await update(examRef, {
            totalQuestions: (examSnapshot.val().totalQuestions || 1) - 1
          });
        }
        
        setSuccess('Question deleted successfully!');
        fetchQuestions(examId);
        fetchExams();
      } catch (err) {
        setError('Error deleting question: ' + err.message);
      }
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    if (!currentUser) {
      setError('Please login to update exam status');
      return;
    }
    setLoading(true);
    try {
      const examRef = ref(db, `exams/${id}`);
      await update(examRef, { status: newStatus });
      setSuccess('Exam status updated successfully!');
      fetchExams();
    } catch (err) {
      setError('Error updating exam status: ' + err.message);
    }
    setLoading(false);
  };

  const handleEditSubmission = (submission) => {
    setEditingSubmission({...submission});
  };

  const handleSaveSubmission = async () => {
    if (!editingSubmission) return;

    setLoading(true);
    try {
      const submissionRef = ref(db, `examResults/${editingSubmission.id}`);
      
      // Recalculate totals based on edited answers
      const updatedQuestionResults = editingSubmission.questionResults.map(q => ({
        ...q,
        isCorrect: q.studentAnswer === q.correctAnswer,
        marksObtained: q.studentAnswer === q.correctAnswer ? q.marks : 0
      }));

      const totalScore = updatedQuestionResults.reduce((sum, q) => sum + q.marksObtained, 0);
      const totalMarks = updatedQuestionResults.reduce((sum, q) => sum + parseInt(q.marks), 0);
      const percentage = (totalScore / totalMarks) * 100;
      const correctAnswers = updatedQuestionResults.filter(q => q.isCorrect).length;
      const wrongAnswers = updatedQuestionResults.filter(q => !q.isCorrect && q.studentAnswer !== 'Not attempted').length;
      const notAttempted = updatedQuestionResults.filter(q => q.studentAnswer === 'Not attempted').length;

      const updatedSubmission = {
        ...editingSubmission,
        questionResults: updatedQuestionResults,
        score: totalScore,
        totalMarks: totalMarks,
        percentage: percentage,
        correctAnswers: correctAnswers,
        wrongAnswers: wrongAnswers,
        notAttempted: notAttempted,
        grade: calculateGrade(percentage),
        status: percentage >= 40 ? 'PASS' : 'FAIL',
        lastModified: new Date().toISOString(),
        modifiedBy: currentUser.uid
      };

      await update(submissionRef, updatedSubmission);
      
      setStudentSubmissions(prev => 
        prev.map(sub => sub.id === editingSubmission.id ? updatedSubmission : sub)
      );
      
      setEditingSubmission(null);
      setSuccess('Student submission updated successfully!');
    } catch (err) {
      setError('Error updating submission: ' + err.message);
    }
    setLoading(false);
  };

  const handleDeleteSubmission = async (submissionId) => {
    if (window.confirm('Are you sure you want to delete this student submission?')) {
      setLoading(true);
      try {
        const submissionRef = ref(db, `examResults/${submissionId}`);
        await remove(submissionRef);
        
        setStudentSubmissions(prev => prev.filter(sub => sub.id !== submissionId));
        setSuccess('Student submission deleted successfully!');
      } catch (err) {
        setError('Error deleting submission: ' + err.message);
      }
      setLoading(false);
    }
  };

  const calculateGrade = (percentage) => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C';
    if (percentage >= 40) return 'D';
    return 'F';
  };

  const generateAdmitCards = (examId) => {
    const exam = exams.find(e => e.id === examId);
    if (!exam) return;
    
    alert(`Admit cards generated for ${exam.examName}. Students can now download them.`);
    setSuccess(`Admit cards for ${exam.examName} have been generated successfully!`);
  };

  const showDetailedResult = (result) => {
    setSelectedResult(result);
    setShowResultDetails(true);
  };

  const printResult = (result) => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Result - ${result.studentName}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            margin: 20px; 
            background: white;
            color: black;
          }
          .header { 
            text-align: center; 
            border-bottom: 3px double #333;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .school-name { 
            font-size: 28px; 
            font-weight: bold; 
            margin-bottom: 5px;
            color: #2c3e50;
          }
          .school-address { 
            font-size: 14px; 
            color: #666;
            margin-bottom: 10px;
          }
          .result-title { 
            font-size: 24px; 
            margin: 20px 0; 
            color: #2c3e50;
          }
          .student-info { 
            margin: 20px 0; 
            padding: 15px;
            background: #f8f9fa;
            border-left: 4px solid #007bff;
          }
          .info-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 20px 0;
          }
          .info-table td { 
            padding: 8px; 
            border: 1px solid #ddd;
          }
          .info-table tr:nth-child(even) { 
            background: #f8f9fa; 
          }
          .performance-grid { 
            display: grid; 
            grid-template-columns: repeat(4, 1fr); 
            gap: 15px; 
            margin: 25px 0;
          }
          .performance-card { 
            padding: 15px; 
            text-align: center; 
            border-radius: 8px;
            color: white;
            font-weight: bold;
          }
          .score-card { background: #007bff; }
          .percentage-card { background: #28a745; }
          .grade-card { background: #6f42c1; }
          .status-card { background: #fd7e14; }
          .summary-grid { 
            display: grid; 
            grid-template-columns: repeat(3, 1fr); 
            gap: 15px; 
            margin: 20px 0;
          }
          .summary-card { 
            padding: 15px; 
            text-align: center; 
            border-radius: 8px;
            color: white;
            font-weight: bold;
          }
          .correct-card { background: #28a745; }
          .wrong-card { background: #dc3545; }
          .not-attempted-card { background: #ffc107; color: #000; }
          .question-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 20px 0;
          }
          .question-table th, .question-table td { 
            padding: 10px; 
            border: 1px solid #ddd; 
            text-align: left;
          }
          .question-table th { 
            background: #343a40; 
            color: white;
          }
          .footer { 
            text-align: center; 
            margin-top: 40px; 
            padding-top: 20px;
            border-top: 2px solid #333;
            font-size: 12px;
            color: #666;
          }
          .signature-area { 
            margin-top: 50px; 
            display: flex; 
            justify-content: space-between;
          }
          .signature-box { 
            text-align: center; 
            width: 200px;
            border-top: 1px solid #333;
            padding-top: 10px;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="school-name">SCHOOL NAME</div>
          <div class="school-address">School Address, City, State - PIN Code</div>
          <div class="school-address">Phone: +91 XXXXXXXXXX | Email: school@email.com</div>
          <div class="result-title">EXAMINATION RESULT</div>
        </div>

        <div class="student-info">
          <table class="info-table">
            <tr>
              <td><strong>Student Name:</strong></td>
              <td>${result.studentName}</td>
              <td><strong>Roll Number:</strong></td>
              <td>${result.studentRollNumber}</td>
            </tr>
            <tr>
              <td><strong>Class & Section:</strong></td>
              <td>${result.studentClass} - ${result.studentSection}</td>
              <td><strong>Exam Date:</strong></td>
              <td>${new Date(result.submittedAt).toLocaleDateString()}</td>
            </tr>
            <tr>
              <td><strong>Exam Name:</strong></td>
              <td>${result.examName}</td>
              <td><strong>Subject:</strong></td>
              <td>${result.subject}</td>
            </tr>
          </table>
        </div>

        <div class="performance-grid">
          <div class="performance-card score-card">
            <div style="font-size: 24px;">${result.score}/${result.totalMarks}</div>
            <div>SCORE</div>
          </div>
          <div class="performance-card percentage-card">
            <div style="font-size: 24px;">${result.percentage?.toFixed(1) || ((result.score / result.totalMarks) * 100).toFixed(1)}%</div>
            <div>PERCENTAGE</div>
          </div>
          <div class="performance-card grade-card">
            <div style="font-size: 24px;">${result.grade}</div>
            <div>GRADE</div>
          </div>
          <div class="performance-card status-card">
            <div style="font-size: 24px;">${result.status}</div>
            <div>STATUS</div>
          </div>
        </div>

        <div class="summary-grid">
          <div class="summary-card correct-card">
            <div style="font-size: 20px;">${result.correctAnswers || 0}</div>
            <div>CORRECT ANSWERS</div>
          </div>
          <div class="summary-card wrong-card">
            <div style="font-size: 20px;">${result.wrongAnswers || 0}</div>
            <div>WRONG ANSWERS</div>
          </div>
          <div class="summary-card not-attempted-card">
            <div style="font-size: 20px;">${result.notAttempted || 0}</div>
            <div>NOT ATTEMPTED</div>
          </div>
        </div>

        ${result.questionResults ? `
        <h3 style="color: #2c3e50; margin: 30px 0 15px 0;">Question-wise Analysis</h3>
        <table class="question-table">
          <thead>
            <tr>
              <th>Q.No</th>
              <th>Your Answer</th>
              <th>Correct Answer</th>
              <th>Marks Obtained</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${result.questionResults.map((question, index) => `
              <tr>
                <td><strong>${index + 1}</strong></td>
                <td style="color: ${question.isCorrect ? 'green' : (question.studentAnswer === 'Not attempted' ? 'orange' : 'red')}; font-weight: bold;">
                  ${question.studentAnswer}
                </td>
                <td style="color: green; font-weight: bold;">${question.correctAnswer}</td>
                <td>${question.marksObtained}/${question.marks}</td>
                <td>
                  <span style="color: ${question.isCorrect ? 'green' : (question.studentAnswer === 'Not attempted' ? 'orange' : 'red')}; font-weight: bold;">
                    ${question.isCorrect ? 'CORRECT' : (question.studentAnswer === 'Not attempted' ? 'NOT ATTEMPTED' : 'WRONG')}
                  </span>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        ` : ''}

        <div class="signature-area">
          <div class="signature-box">
            <div>Class Teacher</div>
          </div>
          <div class="signature-box">
            <div>Principal</div>
          </div>
        </div>

        <div class="footer">
          <p>This is a computer generated result. No signature is required.</p>
          <p>Generated on: ${new Date().toLocaleString()}</p>
        </div>

        <div class="no-print" style="text-align: center; margin-top: 20px;">
          <button onclick="window.print()" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer;">
            Print Result
          </button>
          <button onclick="window.close()" style="padding: 10px 20px; background: #dc3545; color: white; border: none; border-radius: 5px; cursor: pointer; margin-left: 10px;">
            Close Window
          </button>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const filteredSubmissions = studentSubmissions.filter(submission =>
    submission.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    submission.studentRollNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    submission.studentClass.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderStudentSubmissionsModal = () => {
    return (
      <Modal show={showSubmissionsModal} onHide={() => setShowSubmissionsModal(false)} size="xl" centered>
        <Modal.Header closeButton className="bg-info text-white">
          <Modal.Title>
            <FaFileAlt className="me-2" />
            Student Submissions - {selectedExamForResults?.examName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Total Submissions: {studentSubmissions.length}</h5>
            <InputGroup style={{ width: '300px' }}>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search by name, roll number, or class..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>

          {loading ? (
            <div className="text-center">
              <Spinner animation="border" /> Loading submissions...
            </div>
          ) : filteredSubmissions.length > 0 ? (
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <Table responsive striped bordered>
                <thead className="bg-light sticky-top">
                  <tr>
                    <th>Student Name</th>
                    <th>Roll No</th>
                    <th>Class-Section</th>
                    <th>Score</th>
                    <th>Percentage</th>
                    <th>Status</th>
                    <th>Submitted At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubmissions.map((submission, index) => (
                    <tr key={submission.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaUser className="me-2 text-muted" />
                          {submission.studentName}
                        </div>
                      </td>
                      <td>{submission.studentRollNumber}</td>
                      <td>{submission.studentClass}-{submission.studentSection}</td>
                      <td>
                        <strong>{submission.score}/{submission.totalMarks}</strong>
                      </td>
                      <td>
                        <Badge bg={submission.percentage >= 40 ? 'success' : 'warning'}>
                          {submission.percentage?.toFixed(1)}%
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={submission.status === 'PASS' ? 'success' : 'danger'}>
                          {submission.status}
                        </Badge>
                      </td>
                      <td>
                        {new Date(submission.submittedAt).toLocaleString()}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditSubmission(submission)}
                            title="Edit Submission"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => showDetailedResult(submission)}
                            title="View Details"
                          >
                            <FaEye />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteSubmission(submission.id)}
                            title="Delete Submission"
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <Alert variant="info" className="text-center">
              No student submissions found.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSubmissionsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const renderEditSubmissionModal = () => {
    if (!editingSubmission) return null;

    return (
      <Modal show={!!editingSubmission} onHide={() => setEditingSubmission(null)} size="lg" centered>
        <Modal.Header closeButton className="bg-warning text-dark">
          <Modal.Title>
            <FaEdit className="me-2" />
            Edit Student Submission - {editingSubmission.studentName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3 p-3 bg-light rounded">
            <h6>Student: {editingSubmission.studentName}</h6>
            <p className="mb-1">Roll No: {editingSubmission.studentRollNumber}</p>
            <p className="mb-0">Class: {editingSubmission.studentClass}-{editingSubmission.studentSection}</p>
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <h6>Question Answers:</h6>
            {editingSubmission.questionResults?.map((question, index) => (
              <Card key={index} className="mb-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="mb-0">Q{index + 1}. {question.question}</h6>
                    <Badge bg="secondary">{question.marks} marks</Badge>
                  </div>
                  
                  <div className="mb-2">
                    <strong>Options:</strong>
                    <div className="ms-2">
                      <div>A. {question.optionA}</div>
                      <div>B. {question.optionB}</div>
                      <div>C. {question.optionC}</div>
                      <div>D. {question.optionD}</div>
                    </div>
                  </div>

                  <Row>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label><strong>Student's Answer:</strong></Form.Label>
                        <Form.Select
                          value={question.studentAnswer}
                          onChange={(e) => {
                            const updatedQuestions = [...editingSubmission.questionResults];
                            updatedQuestions[index].studentAnswer = e.target.value;
                            setEditingSubmission({
                              ...editingSubmission,
                              questionResults: updatedQuestions
                            });
                          }}
                        >
                          <option value="Not attempted">Not Attempted</option>
                          <option value="A">A</option>
                          <option value="B">B</option>
                          <option value="C">C</option>
                          <option value="D">D</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label><strong>Correct Answer:</strong></Form.Label>
                        <Form.Control
                          type="text"
                          value={question.correctAnswer}
                          readOnly
                          className="bg-light"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="mt-2">
                    <Badge bg={question.studentAnswer === question.correctAnswer ? 'success' : 'danger'}>
                      {question.studentAnswer === question.correctAnswer ? 'Correct' : 'Incorrect'}
                    </Badge>
                    <span className="ms-2">
                      Marks: {question.marksObtained}/{question.marks}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>

          <Card className="mt-3">
            <Card.Body className="bg-light">
              <h6>Summary:</h6>
              <Row>
                <Col md={3} className="text-center">
                  <div className="p-2 bg-success text-white rounded">
                    <h5 className="mb-0">
                      {editingSubmission.questionResults?.filter(q => q.studentAnswer === q.correctAnswer).length}
                    </h5>
                    <small>Correct</small>
                  </div>
                </Col>
                <Col md={3} className="text-center">
                  <div className="p-2 bg-danger text-white rounded">
                    <h5 className="mb-0">
                      {editingSubmission.questionResults?.filter(q => q.studentAnswer !== q.correctAnswer && q.studentAnswer !== 'Not attempted').length}
                    </h5>
                    <small>Wrong</small>
                  </div>
                </Col>
                <Col md={3} className="text-center">
                  <div className="p-2 bg-warning text-white rounded">
                    <h5 className="mb-0">
                      {editingSubmission.questionResults?.filter(q => q.studentAnswer === 'Not attempted').length}
                    </h5>
                    <small>Not Attempted</small>
                  </div>
                </Col>
                <Col md={3} className="text-center">
                  <div className="p-2 bg-primary text-white rounded">
                    <h5 className="mb-0">
                      {editingSubmission.questionResults?.reduce((sum, q) => sum + (q.studentAnswer === q.correctAnswer ? parseInt(q.marks) : 0), 0)}/
                      {editingSubmission.questionResults?.reduce((sum, q) => sum + parseInt(q.marks), 0)}
                    </h5>
                    <small>Score</small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingSubmission(null)}>
            <FaTimes className="me-1" /> Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveSubmission} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : <><FaSave className="me-1" /> Save Changes</>}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const renderResultDetails = () => {
    if (!selectedResult) return null;

    return (
      <Modal show={showResultDetails} onHide={() => setShowResultDetails(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <FaChartBar className="me-2" />
            Detailed Result - {selectedResult.studentName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-4">
            <Col md={6}>
              <Card className="bg-light">
                <Card.Body>
                  <h6>Student Information</h6>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td><strong>Name:</strong></td>
                        <td>{selectedResult.studentName}</td>
                      </tr>
                      <tr>
                        <td><strong>Class:</strong></td>
                        <td>{selectedResult.studentClass}-{selectedResult.studentSection}</td>
                      </tr>
                      <tr>
                        <td><strong>Roll No:</strong></td>
                        <td>{selectedResult.studentRollNumber}</td>
                      </tr>
                      <tr>
                        <td><strong>Email:</strong></td>
                        <td>{selectedResult.studentEmail}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="bg-light">
                <Card.Body>
                  <h6>Exam Information</h6>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td><strong>Exam:</strong></td>
                        <td>{selectedResult.examName}</td>
                      </tr>
                      <tr>
                        <td><strong>Subject:</strong></td>
                        <td>{selectedResult.subject}</td>
                      </tr>
                      <tr>
                        <td><strong>Date:</strong></td>
                        <td>{new Date(selectedResult.submittedAt).toLocaleDateString()}</td>
                      </tr>
                      <tr>
                        <td><strong>Time Taken:</strong></td>
                        <td>{Math.floor(selectedResult.timeSpent / 60)}:{(selectedResult.timeSpent % 60).toString().padStart(2, '0')}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={3} className="text-center">
              <Card className="border-success">
                <Card.Body>
                  <h2 className="text-success">{selectedResult.score}/{selectedResult.totalMarks}</h2>
                  <small className="text-muted">Score</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="text-center">
              <Card className="border-primary">
                <Card.Body>
                  <h2 className="text-primary">{selectedResult.percentage?.toFixed(1)}%</h2>
                  <small className="text-muted">Percentage</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="text-center">
              <Card className="border-info">
                <Card.Body>
                  <h2 className="text-info">{selectedResult.grade}</h2>
                  <small className="text-muted">Grade</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="text-center">
              <Card className={selectedResult.status === 'PASS' ? 'border-success' : 'border-danger'}>
                <Card.Body>
                  <h2 className={selectedResult.status === 'PASS' ? 'text-success' : 'text-danger'}>
                    {selectedResult.status}
                  </h2>
                  <small className="text-muted">Status</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card>
            <Card.Header className="bg-info text-white">
              <h6 className="mb-0">Performance Analysis</h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4} className="text-center mb-3">
                  <div className="p-3 bg-success text-white rounded">
                    <h4>{selectedResult.correctAnswers || 0}</h4>
                    <small>Correct Answers</small>
                  </div>
                </Col>
                <Col md={4} className="text-center mb-3">
                  <div className="p-3 bg-danger text-white rounded">
                    <h4>{selectedResult.wrongAnswers || 0}</h4>
                    <small>Wrong Answers</small>
                  </div>
                </Col>
                <Col md={4} className="text-center mb-3">
                  <div className="p-3 bg-warning text-white rounded">
                    <h4>{selectedResult.notAttempted || 0}</h4>
                    <small>Not Attempted</small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {selectedResult.questionResults && (
            <Card className="mt-4">
              <Card.Header className="bg-secondary text-white">
                <h6 className="mb-0">Question-wise Analysis</h6>
              </Card.Header>
              <Card.Body>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <Table striped bordered size="sm">
                    <thead>
                      <tr>
                        <th>Q.No</th>
                        <th>Your Answer</th>
                        <th>Correct Answer</th>
                        <th>Marks</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedResult.questionResults.map((question, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td className={question.isCorrect ? 'text-success' : question.studentAnswer === 'Not attempted' ? 'text-warning' : 'text-danger'}>
                            <strong>{question.studentAnswer}</strong>
                          </td>
                          <td className="text-success">
                            <strong>{question.correctAnswer}</strong>
                          </td>
                          <td>{question.marksObtained}/{question.marks}</td>
                          <td>
                            <Badge bg={
                              question.isCorrect ? 'success' : 
                              question.studentAnswer === 'Not attempted' ? 'warning' : 'danger'
                            }>
                              {question.isCorrect ? 'Correct' : 
                               question.studentAnswer === 'Not attempted' ? 'Not Attempted' : 'Wrong'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}

          <div className="d-flex justify-content-center mt-4">
            <Button 
              variant="success" 
              size="lg"
              onClick={() => printResult(selectedResult)}
              className="me-3"
            >
              <FaPrint className="me-2" />
              Print Result
            </Button>
            <Button 
              variant="secondary" 
              size="lg"
              onClick={() => setShowResultDetails(false)}
            >
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  const renderEditQuestionModal = () => {
    return (
      <Modal show={showEditQuestionModal} onHide={() => setShowEditQuestionModal(false)} size="lg">
        <Modal.Header closeButton className="bg-warning text-dark">
          <Modal.Title>Edit Question</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateQuestion}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Question</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3}
                placeholder="Enter question"
                value={questionFormData.question}
                onChange={(e) => setQuestionFormData({...questionFormData, question: e.target.value})}
                required
              />
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Option A</Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="Option A"
                    value={questionFormData.optionA}
                    onChange={(e) => setQuestionFormData({...questionFormData, optionA: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Option B</Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="Option B"
                    value={questionFormData.optionB}
                    onChange={(e) => setQuestionFormData({...questionFormData, optionB: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Option C</Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="Option C"
                    value={questionFormData.optionC}
                    onChange={(e) => setQuestionFormData({...questionFormData, optionC: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Option D</Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="Option D"
                    value={questionFormData.optionD}
                    onChange={(e) => setQuestionFormData({...questionFormData, optionD: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Correct Answer</Form.Label>
                  <Form.Select 
                    value={questionFormData.correctAnswer}
                    onChange={(e) => setQuestionFormData({...questionFormData, correctAnswer: e.target.value})}
                    required
                  >
                    <option value="A">Option A</option>
                    <option value="B">Option B</option>
                    <option value="C">Option C</option>
                    <option value="D">Option D</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Marks</Form.Label>
                  <Form.Control 
                    type="number" 
                    min="1"
                    value={questionFormData.marks}
                    onChange={(e) => setQuestionFormData({...questionFormData, marks: parseInt(e.target.value)})}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditQuestionModal(false)}>
              Cancel
            </Button>
            <Button variant="warning" type="submit" disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : 'Update Question'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'createExam':
        return (
          <Card className="shadow-sm">
            <Card.Body>
              <h4 className="mb-4">Create New Exam</h4>
              <Form onSubmit={handleCreateExam}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Exam Name</Form.Label>
                      <Form.Control 
                        type="text" 
                        placeholder="Enter exam name"
                        value={examFormData.examName}
                        onChange={(e) => setExamFormData({...examFormData, examName: e.target.value})}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Subject</Form.Label>
                      <Form.Control 
                        type="text" 
                        placeholder="Enter subject"
                        value={examFormData.subject}
                        onChange={(e) => setExamFormData({...examFormData, subject: e.target.value})}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Class</Form.Label>
                      <Form.Control 
                        type="text" 
                        placeholder="e.g., 10th"
                        value={examFormData.class}
                        onChange={(e) => setExamFormData({...examFormData, class: e.target.value})}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Section</Form.Label>
                      <Form.Control 
                        type="text" 
                        placeholder="e.g., A"
                        value={examFormData.section}
                        onChange={(e) => setExamFormData({...examFormData, section: e.target.value})}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date</Form.Label>
                      <Form.Control 
                        type="date" 
                        value={examFormData.date}
                        onChange={(e) => setExamFormData({...examFormData, date: e.target.value})}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Duration (minutes)</Form.Label>
                      <Form.Control 
                        type="number" 
                        placeholder="Enter duration"
                        value={examFormData.duration}
                        onChange={(e) => setExamFormData({...examFormData, duration: e.target.value})}
                        min="1"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Total Marks</Form.Label>
                      <Form.Control 
                        type="number" 
                        placeholder="Enter total marks"
                        value={examFormData.totalMarks}
                        onChange={(e) => setExamFormData({...examFormData, totalMarks: e.target.value})}
                        min="1"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Passing Marks</Form.Label>
                      <Form.Control 
                        type="number" 
                        placeholder="Enter passing marks"
                        value={examFormData.passingMarks}
                        onChange={(e) => setExamFormData({...examFormData, passingMarks: e.target.value})}
                        min="0"
                        max={examFormData.totalMarks || 100}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? <Spinner animation="border" size="sm" /> : 'Create Exam'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        );

      case 'manageExams':
        return (
          <Card className="shadow-sm">
            <Card.Body>
              <h4 className="mb-4">Manage Exams</h4>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Exam Name</th>
                    <th>Subject</th>
                    <th>Class-Section</th>
                    <th>Date</th>
                    <th>Duration</th>
                    <th>Questions</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="text-center">
                        <Spinner animation="border" size="sm" /> Loading...
                      </td>
                    </tr>
                  ) : exams.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center">No exams found</td>
                    </tr>
                  ) : exams.map(exam => (
                    <tr key={exam.id}>
                      <td>{exam.examName}</td>
                      <td>{exam.subject}</td>
                      <td>{exam.class}-{exam.section}</td>
                      <td>{exam.date}</td>
                      <td>{exam.duration} mins</td>
                      <td>{exam.totalQuestions || 0}</td>
                      <td>
                        <Badge bg={
                          exam.status === 'Upcoming' ? 'warning' :
                          exam.status === 'Published' ? 'success' :
                          'secondary'
                        }>
                          {exam.status}
                        </Badge>
                      </td>
                      <td>
                        <Button 
                          variant="outline-success" 
                          size="sm" 
                          className="me-2 mb-1"
                          onClick={() => handleUpdateStatus(exam.id, 'Published')}
                          disabled={exam.status === 'Published'}
                        >
                          <FaCheck /> Publish
                        </Button>
                        <Button 
                          variant="outline-info" 
                          size="sm"
                          className="me-2 mb-1"
                          onClick={() => {
                            setSelectedExam(exam);
                            setActiveTab('paperCreation');
                          }}
                        >
                          <FaQuestionCircle /> Questions
                        </Button>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          className="me-2 mb-1"
                          onClick={() => fetchStudentSubmissions(exam.id)}
                        >
                          <FaFileAlt /> Submissions
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          className="mb-1"
                          onClick={() => handleDeleteExam(exam.id)}
                        >
                          <FaTrash /> Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        );

      case 'paperCreation':
        return (
          <div>
            {selectedExam ? (
              <Card className="shadow-sm mb-4">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4>Question Paper: {selectedExam.examName}</h4>
                    <Button 
                      variant="primary" 
                      onClick={() => setShowQuestionModal(true)}
                    >
                      <FaPlus /> Add Question
                    </Button>
                  </div>
                  
                  <Row className="mb-4">
                    <Col md={3}>
                      <Card className="bg-light">
                        <Card.Body className="py-2">
                          <small className="text-muted">Total Questions</small>
                          <h5 className="mb-0">{questions.length}</h5>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="bg-light">
                        <Card.Body className="py-2">
                          <small className="text-muted">Total Marks</small>
                          <h5 className="mb-0">
                            {questions.reduce((total, q) => total + parseInt(q.marks || 1), 0)}
                          </h5>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="bg-light">
                        <Card.Body className="py-2">
                          <small className="text-muted">Exam Date</small>
                          <h5 className="mb-0">{selectedExam.date}</h5>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="bg-light">
                        <Card.Body className="py-2">
                          <small className="text-muted">Duration</small>
                          <h5 className="mb-0">{selectedExam.duration} mins</h5>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  
                  {questions.length === 0 ? (
                    <Alert variant="info" className="text-center">
                      No questions added yet. Click "Add Question" to start creating your exam paper.
                    </Alert>
                  ) : (
                    <Table responsive striped>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Question</th>
                          <th>Options</th>
                          <th>Correct Answer</th>
                          <th>Marks</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {questions.map((q, index) => (
                          <tr key={q.id}>
                            <td>{index + 1}</td>
                            <td>{q.question}</td>
                            <td>
                              <div>A. {q.optionA}</div>
                              <div>B. {q.optionB}</div>
                              <div>C. {q.optionC}</div>
                              <div>D. {q.optionD}</div>
                            </td>
                            <td>{q.correctAnswer}</td>
                            <td>{q.marks}</td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button 
                                  variant="outline-warning" 
                                  size="sm"
                                  onClick={() => handleEditQuestion(q)}
                                  title="Edit Question"
                                >
                                  <FaEdit />
                                </Button>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm"
                                  onClick={() => handleDeleteQuestion(selectedExam.id, q.id)}
                                  title="Delete Question"
                                >
                                  <FaTrash />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            ) : (
              <Alert variant="warning">
                Please select an exam from the "Manage Exams" tab to create questions.
              </Alert>
            )}
            
            {/* Add Question Modal */}
            <Modal show={showQuestionModal} onHide={() => setShowQuestionModal(false)} size="lg">
              <Modal.Header closeButton>
                <Modal.Title>Add Question to {selectedExam?.examName}</Modal.Title>
              </Modal.Header>
              <Form onSubmit={handleAddQuestion}>
                <Modal.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Question</Form.Label>
                    <Form.Control 
                      as="textarea" 
                      rows={3}
                      placeholder="Enter question"
                      value={questionFormData.question}
                      onChange={(e) => setQuestionFormData({...questionFormData, question: e.target.value})}
                      required
                    />
                  </Form.Group>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Option A</Form.Label>
                        <Form.Control 
                          type="text" 
                          placeholder="Option A"
                          value={questionFormData.optionA}
                          onChange={(e) => setQuestionFormData({...questionFormData, optionA: e.target.value})}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Option B</Form.Label>
                        <Form.Control 
                          type="text" 
                          placeholder="Option B"
                          value={questionFormData.optionB}
                          onChange={(e) => setQuestionFormData({...questionFormData, optionB: e.target.value})}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Option C</Form.Label>
                        <Form.Control 
                          type="text" 
                          placeholder="Option C"
                          value={questionFormData.optionC}
                          onChange={(e) => setQuestionFormData({...questionFormData, optionC: e.target.value})}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Option D</Form.Label>
                        <Form.Control 
                          type="text" 
                          placeholder="Option D"
                          value={questionFormData.optionD}
                          onChange={(e) => setQuestionFormData({...questionFormData, optionD: e.target.value})}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Correct Answer</Form.Label>
                        <Form.Select 
                          value={questionFormData.correctAnswer}
                          onChange={(e) => setQuestionFormData({...questionFormData, correctAnswer: e.target.value})}
                          required
                        >
                          <option value="A">Option A</option>
                          <option value="B">Option B</option>
                          <option value="C">Option C</option>
                          <option value="D">Option D</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Marks</Form.Label>
                        <Form.Control 
                          type="number" 
                          min="1"
                          value={questionFormData.marks}
                          onChange={(e) => setQuestionFormData({...questionFormData, marks: parseInt(e.target.value)})}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowQuestionModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : 'Add Question'}
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal>

            {/* Edit Question Modal */}
            {renderEditQuestionModal()}
          </div>
        );

      case 'studentSubmissions':
        return (
          <Card className="shadow-sm">
            <Card.Body>
              <h4 className="mb-4">Student Submissions Management</h4>
              
              <Tabs defaultActiveKey="exams" className="mb-3">
                <Tab eventKey="exams" title="Select Exam">
                  <div className="p-3 border rounded mt-3">
                    <h5>Available Exams with Submissions</h5>
                    <Row>
                      {exams.filter(exam => exam.status === 'Published').length === 0 ? (
                        <Col md={12}>
                          <Alert variant="info" className="text-center">
                            No published exams available.
                          </Alert>
                        </Col>
                      ) : (
                        exams.filter(exam => exam.status === 'Published').map(exam => (
                          <Col md={4} key={exam.id} className="mb-3">
                            <Card className="h-100">
                              <Card.Body className="d-flex flex-column">
                                <h6>{exam.examName}</h6>
                                <p className="mb-1"><small>Subject: {exam.subject}</small></p>
                                <p className="mb-1"><small>Class: {exam.class}-{exam.section}</small></p>
                                <p className="mb-1"><small>Date: {exam.date}</small></p>
                                <p className="mb-2"><small>Total Marks: {exam.totalMarks}</small></p>
                                <div className="mt-auto">
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    className="me-2 mb-1"
                                    onClick={() => fetchStudentSubmissions(exam.id)}
                                    disabled={loading}
                                  >
                                    {loading ? <Spinner animation="border" size="sm" /> : 'View Submissions'}
                                  </Button>
                                  <Button 
                                    variant="outline-info" 
                                    size="sm"
                                    className="mb-1"
                                    onClick={() => fetchResults(exam.id)}
                                  >
                                    <FaChartBar /> Results
                                  </Button>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))
                      )}
                    </Row>
                  </div>
                </Tab>
              </Tabs>

              {selectedExamForResults && studentSubmissions.length > 0 && (
                <Card className="mt-3">
                  <Card.Header className="bg-success text-white">
                    <h6 className="mb-0">
                      Submission Summary - {selectedExamForResults.examName}
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={3} className="text-center">
                        <div className="p-3 bg-primary text-white rounded">
                          <h4>{studentSubmissions.length}</h4>
                          <small>Total Submissions</small>
                        </div>
                      </Col>
                      <Col md={3} className="text-center">
                        <div className="p-3 bg-success text-white rounded">
                          <h4>{studentSubmissions.filter(s => s.status === 'PASS').length}</h4>
                          <small>Passed</small>
                        </div>
                      </Col>
                      <Col md={3} className="text-center">
                        <div className="p-3 bg-danger text-white rounded">
                          <h4>{studentSubmissions.filter(s => s.status === 'FAIL').length}</h4>
                          <small>Failed</small>
                        </div>
                      </Col>
                      <Col md={3} className="text-center">
                        <div className="p-3 bg-info text-white rounded">
                          <h4>
                            {studentSubmissions.length > 0 
                              ? (studentSubmissions.reduce((sum, s) => sum + s.percentage, 0) / studentSubmissions.length).toFixed(1)
                              : 0
                            }%
                          </h4>
                          <small>Average %</small>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}
            </Card.Body>
          </Card>
        );

      case 'results':
        return (
          <Card className="shadow-sm">
            <Card.Body>
              <h4 className="mb-4">Exam Results</h4>
              
              <Tabs defaultActiveKey="exams" className="mb-3">
                <Tab eventKey="exams" title="Select Exam">
                  <div className="p-3 border rounded mt-3">
                    <h5>Available Exams</h5>
                    <Row>
                      {exams.filter(exam => exam.status === 'Published').length === 0 ? (
                        <Col md={12}>
                          <Alert variant="info" className="text-center">
                            No published exams available.
                          </Alert>
                        </Col>
                      ) : (
                        exams.filter(exam => exam.status === 'Published').map(exam => (
                          <Col md={4} key={exam.id} className="mb-3">
                            <Card className="h-100">
                              <Card.Body>
                                <h6>{exam.examName}</h6>
                                <p className="mb-1"><small>Subject: {exam.subject}</small></p>
                                <p className="mb-1"><small>Class: {exam.class}-{exam.section}</small></p>
                                <p className="mb-1"><small>Date: {exam.date}</small></p>
                                <p className="mb-2"><small>Total Marks: {exam.totalMarks}</small></p>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  onClick={() => fetchResults(exam.id)}
                                  disabled={loading}
                                >
                                  {loading ? <Spinner animation="border" size="sm" /> : 'View Results'}
                                </Button>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))
                      )}
                    </Row>
                  </div>
                </Tab>
                
                <Tab eventKey="results" title="View Results" disabled={!selectedExamForResults}>
                  <div className="p-3 border rounded mt-3">
                    {selectedExamForResults && (
                      <div className="mb-3 p-3 bg-light rounded">
                        <h5>{selectedExamForResults.examName}</h5>
                        <p className="mb-1">Subject: {selectedExamForResults.subject}</p>
                        <p className="mb-1">Class: {selectedExamForResults.class}-{selectedExamForResults.section}</p>
                        <p className="mb-1">Date: {selectedExamForResults.date}</p>
                        <p className="mb-0">Total Marks: {selectedExamForResults.totalMarks}</p>
                      </div>
                    )}
                    
                    {loading ? (
                      <div className="text-center">
                        <Spinner animation="border" /> Loading results...
                      </div>
                    ) : results.length > 0 ? (
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6>Student Results ({results.length} students)</h6>
                          <Badge bg="info">
                            Pass: {results.filter(r => r.status === 'PASS').length} | 
                            Fail: {results.filter(r => r.status === 'FAIL').length}
                          </Badge>
                        </div>
                        <Table responsive striped>
                          <thead>
                            <tr>
                              <th>Student Name</th>
                              <th>Class-Section</th>
                              <th>Roll No</th>
                              <th>Score</th>
                              <th>Percentage</th>
                              <th>Grade</th>
                              <th>Status</th>
                              <th>Time Taken</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.map(result => {
                              const percentage = result.percentage?.toFixed(1) || ((result.score / result.totalMarks) * 100).toFixed(1);
                              const isPass = percentage >= 40;
                              
                              return (
                                <tr key={result.id}>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <FaUser className="me-2 text-muted" />
                                      {result.studentName}
                                    </div>
                                  </td>
                                  <td>{result.studentClass}-{result.studentSection}</td>
                                  <td>{result.studentRollNumber}</td>
                                  <td>
                                    <strong>{result.score}/{result.totalMarks}</strong>
                                  </td>
                                  <td>
                                    <Badge bg={isPass ? 'success' : 'warning'}>
                                      {percentage}%
                                    </Badge>
                                  </td>
                                  <td>
                                    <Badge bg={
                                      result.grade === 'A+' ? 'success' : 
                                      result.grade === 'A' ? 'primary' :
                                      result.grade === 'B+' ? 'info' :
                                      result.grade === 'B' ? 'warning' :
                                      result.grade === 'C' ? 'secondary' :
                                      result.grade === 'D' ? 'light' : 'danger'
                                    }>
                                      {result.grade}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Badge bg={isPass ? 'success' : 'danger'}>
                                      {isPass ? 'PASS' : 'FAIL'}
                                    </Badge>
                                  </td>
                                  <td>
                                    {Math.floor(result.timeSpent / 60)}:{(result.timeSpent % 60).toString().padStart(2, '0')}
                                  </td>
                                  <td>
                                    <div className="d-flex gap-1">
                                      <Button 
                                        variant="outline-info" 
                                        size="sm"
                                        onClick={() => showDetailedResult(result)}
                                      >
                                        <FaChartBar /> Details
                                      </Button>
                                      <Button 
                                        variant="outline-success" 
                                        size="sm"
                                        onClick={() => printResult(result)}
                                      >
                                        <FaPrint /> Print
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <Alert variant="info" className="text-center">
                        No results found for this exam.
                      </Alert>
                    )}
                  </div>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        );

      case 'admitCards':
        return (
          <Card className="shadow-sm">
            <Card.Body>
              <h4 className="mb-4">Generate Admit Cards</h4>
              
              <Row>
                {exams.filter(exam => exam.status === 'Published').length === 0 ? (
                  <Col md={12}>
                    <Alert variant="info" className="text-center">
                      No published exams available for admit card generation.
                    </Alert>
                  </Col>
                ) : (
                  exams.filter(exam => exam.status === 'Published').map(exam => (
                    <Col md={4} key={exam.id} className="mb-4">
                      <Card>
                        <Card.Body>
                          <div className="text-center mb-3">
                            <FaGraduationCap size={40} className="text-primary" />
                          </div>
                          <h5>{exam.examName}</h5>
                          <p><FaClock className="me-2" />{exam.date} | {exam.duration} mins</p>
                          <p><FaFileAlt className="me-2" />{exam.subject}</p>
                          <p><FaUser className="me-2" />{exam.class}-{exam.section}</p>
                          
                          <div className="d-grid gap-2">
                            <Button 
                              variant="primary"
                              onClick={() => generateAdmitCards(exam.id)}
                            >
                              <FaDownload className="me-2" /> Generate Admit Cards
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))
                )}
              </Row>
            </Card.Body>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-3">
      <h2 className="mb-4">Exam Management System</h2>
      
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}
      
      {/* Navigation Tabs */}
      <Row className="mb-4">
        {tabs.map(tab => (
          <Col key={tab.id} xs={6} md={2} className="mb-2">
            <Button
              variant={activeTab === tab.id ? 'primary' : 'outline-primary'}
              className="w-100 d-flex flex-column align-items-center py-3"
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id !== 'results' && tab.id !== 'studentSubmissions') {
                  setSelectedExamForResults(null);
                  setResults([]);
                  setStudentSubmissions([]);
                }
              }}
            >
              <span className="mb-1">{tab.icon}</span>
              <small>{tab.label}</small>
            </Button>
          </Col>
        ))}
      </Row>

      {/* Content Area */}
      {renderContent()}

      {/* Student Submissions Modal */}
      {renderStudentSubmissionsModal()}

      {/* Edit Submission Modal */}
      {renderEditSubmissionModal()}

      {/* Result Details Modal */}
      {renderResultDetails()}

      <style>{`
        .btn-outline-primary:hover {
          background-color: #1976d2;
          color: white;
        }
        .badge {
          font-size: 0.85em;
          padding: 0.5em 0.8em;
        }
        .nav-tabs .nav-link {
          color: #495057;
        }
        .nav-tabs .nav-link.active {
          font-weight: 600;
        }
        .table td {
          vertical-align: middle;
        }
        .sticky-top {
          position: sticky;
          top: 0;
          background: white;
          z-index: 1;
        }
      `}</style>
    </div>
  );
};

export default ExamManagement;